import type { Run, RunStep } from '@ankor-io/common/lang/backend/pipeline/types'
import type { ListOrganizationsRequest, OrganizationLighthouse } from '@ankor-io/common/types/account'
import type { AWXCardResponse, AwxConnectedAccountType } from '@ankor-io/common/types/awx'
import { type CardPayload, type ReconciledCardPayload } from '@ankor-io/common/types/cards'
import type { QuickBooksSyncJobPayload } from '@ankor-io/common/types/integrations/quickbooks'
import type { IntegrationPayload } from '@ankor-io/common/types/integrations/types'
import {
  type XeroConnectionPayload,
  type XeroConnectionSyncRequest,
  type XeroIntegrationPipelinePayload,
  type XeroIntegrationPipelineStageLogPayload,
  type XeroIntegrationPipelineTransactionPayload,
  type XeroOrganizationOverview,
  type XeroSyncJobPayload,
  type XeroSyncJobRunDetailPayload,
  type XeroSyncJobRunPayload,
  type XeroSyncJobRunStepPayload,
} from '@ankor-io/common/types/integrations/xero/types'
import type {
  LighthouseGlobalAccountPayload,
  OnboardingHealthPayload,
  TransactionsStatsPayload,
  TransactionVolumePayload,
  VesselMetricsPayload,
} from '@ankor-io/common/types/lighthouse'
import type { Vessel, VesselAndConnectedAccounts, VesselConnectedAccountAccess } from '@ankor-io/common/types/pay'
import type {
  FeeSource,
  OrganizationFeesPayload,
  SystemFeesPayload,
  UpdateOrganizationPayFeesBody,
} from '@ankor-io/common/types/pay/fee'
import type {
  GeneratedReportStatus,
  ReportGenerationJobPayload,
  ReportGenerationPipelineData,
} from '@ankor-io/common/types/reports/generated-report'
import type {
  FeatureToggleName,
  FeatureToggles,
  OrganizationFeatureTogglesPayload,
  UpdateFeatureTogglesBody,
  UpdateOrganizationTogglesBody,
} from '@ankor-io/common/types/system'

import { type HttpConfig } from '@/api/client/http'

import { http, unwrap } from '../client'
import { buildQueryParams } from '../utils'

interface ListOrganizationsResponse {
  orgs: OrganizationLighthouse[]
}

/**
 * Parse the file name from a Content-Disposition header (e.g. attachment; filename="statement.pdf").
 */
function parseFileName(contentDisposition?: string): string | null {
  const match = contentDisposition?.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i)
  if (!match) return null

  const fileName = match[1].trim()
  try {
    return decodeURIComponent(fileName)
  } catch {
    // a literal % in the name is not valid percent-encoding, keep the name as sent
    return fileName
  }
}

type ListVesselsParams = {
  take?: number
  skip?: number
}

/** Same shape as CreateVesselSchema in pay-endpoint create.vessel.handler */
type CreateVesselBody = {
  name: string
  type?: AwxConnectedAccountType
  entityId?: string
}

/**
 * Lighthouse service for admin operations.
 * Encapsulates all Lighthouse-related API calls with type safety.
 */
export const lighthouseService = {
  /**
   * Fetches a list of all organizations in the system.
   * Used in the Lighthouse admin interface to view and manage organizations.
   *
   * @param request - Optional parameters for pagination and filtering
   * @returns Promise resolving to array of OrganizationLighthouse objects
   * @throws Error if the API request fails
   *
   * @example
   * ```ts
   * const orgs = await lighthouseService.listOrganizations({
   *   take: 50,
   *   skip: 0,
   *   visibility: ['visible']
   * })
   * ```
   */
  async listOrganizations(request: ListOrganizationsRequest = {}): Promise<OrganizationLighthouse[]> {
    const { take = 20, skip = 0, visibility, organizationName, organizationId, vesselName } = request

    const queryParams = buildQueryParams({
      take,
      skip,
      visibility: visibility?.length ? visibility.join(',') : undefined,
      organizationName: organizationName || undefined,
      organizationId: organizationId || undefined,
      vesselName: vesselName || undefined,
    })

    const response = await http.get<ListOrganizationsResponse>(
      `/api/lighthouse/organizations?${queryParams.toString()}`,
    )
    return unwrap(response).orgs
  },

  /**
   * Fetches a single organization by its ID.
   * Used in the Lighthouse admin interface to view and manage a single organization.
   *
   * @param id - The ID of the organization to fetch
   * @returns Promise resolving to OrganizationLighthouse object
   * @throws Error if the API request fails
   *
   * @example
   * ```ts
   * const org = await lighthouseService.getOrganization('123')
   * console.log(org)
   * ```
   */
  async getOrganization(id: string): Promise<OrganizationLighthouse> {
    const response = await http.get<OrganizationLighthouse>(`/api/lighthouse/organizations/${id}`)
    return unwrap(response)
  },

  /**
   * Fetches the feature toggles for a specific organization.
   * Used in the Lighthouse admin interface to view and manage the feature toggles for a specific organization.
   *
   * @param id - The ID of the organization to fetch the feature toggles for
   * @returns Promise resolving to OrganizationFeatureTogglesResponse object with detailed toggle info
   * @throws Error if the API request fails
   *
   * @example
   * ```ts
   * const toggles = await lighthouseService.getOrganizationToggles('123')
   * console.log(toggles.toggles.TEST_FEATURE_TOGGLE.globalDefault) // 'enabled' or 'disabled'
   * console.log(toggles.toggles.TEST_FEATURE_TOGGLE.orgValue) // 'enabled', 'disabled', or null
   * ```
   */
  async getOrganizationToggles(id: string): Promise<OrganizationFeatureTogglesPayload> {
    const response = await http.get<OrganizationFeatureTogglesPayload>(`/api/lighthouse/organizations/${id}/toggles`)
    return unwrap(response)
  },

  /**
   * Updates the feature toggles for a specific organization.
   * Used in the Lighthouse admin interface to update the feature toggles for a specific organization.
   *
   * @param id - The ID of the organization to update the feature toggles for
   * @param body - The feature toggles to update (simple key-value pairs)
   * @returns Promise resolving to OrganizationFeatureTogglesResponse with updated toggles
   * @throws Error if the API request fails
   *
   * @example
   * ```ts
   * await lighthouseService.updateOrganizationToggles('org-123', {
   *   toggles: { TEST_FEATURE_TOGGLE: 'enabled' }
   * })
   * ```
   */
  async updateOrganizationToggles(
    id: string,
    body: UpdateOrganizationTogglesBody,
  ): Promise<OrganizationFeatureTogglesPayload> {
    const response = await http.put<OrganizationFeatureTogglesPayload>(
      `/api/lighthouse/organizations/${id}/toggles`,
      body,
    )
    return unwrap(response)
  },

  /**
   * Creates the feature toggles for a specific organization.
   * Used in the Lighthouse admin interface to create the feature toggles for a specific organization.
   *
   * @param id - The ID of the organization to create the feature toggles for
   * @param body - The feature toggles to create (simple key-value pairs)
   * @returns Promise resolving to OrganizationFeatureTogglesResponse with created toggles
   * @throws Error if the API request fails
   */
  async createOrganizationToggles(
    id: string,
    body: UpdateOrganizationTogglesBody,
  ): Promise<OrganizationFeatureTogglesPayload> {
    const response = await http.post<OrganizationFeatureTogglesPayload>(
      `/api/lighthouse/organizations/${id}/toggles`,
      body,
    )
    return unwrap(response)
  },

  /**
   * Deletes a specific feature toggle override for an organization.
   * Removes the organization-specific override, reverting the toggle to the global default value.
   *
   * @param id - The ID of the organization
   * @param toggleName - The name of the feature toggle to delete
   * @returns Promise resolving to void
   * @throws Error if the API request fails
   *
   * @example
   * ```ts
   * await lighthouseService.deleteOrganizationToggle('org-123', 'TEST_FEATURE_TOGGLE')
   * ```
   */
  async deleteOrganizationToggle(id: string, toggleName: string): Promise<void> {
    await http.delete(`/api/lighthouse/organizations/${id}/toggles/${toggleName}`)
  },

  async getOrganizationFees(id: string): Promise<OrganizationFeesPayload> {
    const response = await http.get<OrganizationFeesPayload>(`/api/lighthouse/organizations/${id}/fees`)
    return unwrap(response)
  },

  async updateOrganizationFees(id: string, body: UpdateOrganizationPayFeesBody): Promise<OrganizationFeesPayload> {
    const response = await http.put<OrganizationFeesPayload>(`/api/lighthouse/organizations/${id}/fees`, body)
    return unwrap(response)
  },

  async deleteOrganizationFee(id: string, feeSource: FeeSource): Promise<void> {
    await http.delete(`/api/lighthouse/organizations/${id}/fees/${feeSource}`)
  },

  async getGlobalToggles(): Promise<FeatureToggles> {
    const response = await http.get<FeatureToggles>(`/api/lighthouse/toggles`)
    return unwrap(response)
  },
  async updateGlobalToggles(body: UpdateFeatureTogglesBody): Promise<FeatureToggles> {
    const response = await http.put<FeatureToggles>(`/api/lighthouse/toggles`, body)
    return unwrap(response)
  },
  async deleteGlobalToggle(toggleName: FeatureToggleName): Promise<void> {
    await http.delete(`/api/lighthouse/toggles/${toggleName}`)
  },
  async getSystemFees(): Promise<SystemFeesPayload> {
    const response = await http.get<SystemFeesPayload>(`/api/lighthouse/fees`)
    return unwrap(response)
  },

  // ─── Xero / Integrations ─────────────────────────────────────────────────────

  /**
   * List available integrations for the organization (feature-toggle gated catalog from Pay Service).
   * Uses Pay API directly so it works without depending on the Lighthouse API route.
   */
  async listOrganizationIntegrations(orgId: string): Promise<IntegrationPayload[]> {
    const response = await http.get<{ integrations: IntegrationPayload[] }>(
      `/api/lighthouse/organizations/${orgId}/integrations`,
    )
    return unwrap(response).integrations
  },

  async getOrganizationXeroOverview(orgId: string): Promise<XeroOrganizationOverview | null> {
    const response = await http.get<XeroOrganizationOverview | null>(
      `/api/lighthouse/organizations/${orgId}/integrations/xero/overview`,
    )
    return unwrap(response)
  },

  async listOrganizationXeroConnections(orgId: string): Promise<XeroConnectionPayload[]> {
    const response = await http.get<{ connections: XeroConnectionPayload[] }>(
      `/api/lighthouse/organizations/${orgId}/integrations/xero/connections`,
    )
    return unwrap(response).connections
  },

  async listXeroSyncJobs(
    orgId: string,
    connectionId: string,
    params?: { connectedAccountName?: string },
  ): Promise<XeroSyncJobPayload[]> {
    const searchParams = new URLSearchParams()
    if (params?.connectedAccountName) {
      searchParams.set('connectedAccountName', params.connectedAccountName)
    }
    const query = searchParams.toString()
    const url = `/api/lighthouse/organizations/${orgId}/integrations/xero/connections/${connectionId}/jobs`
    const response = await http.get<{ jobs: XeroSyncJobPayload[] }>(query ? `${url}?${query}` : url)
    return unwrap(response).jobs
  },

  async listQuickBooksSyncJobs(orgId: string): Promise<QuickBooksSyncJobPayload[]> {
    const response = await http.get<{ jobs: QuickBooksSyncJobPayload[] }>(
      `/api/lighthouse/organizations/${orgId}/integrations/quickbooks/jobs`,
    )
    return unwrap(response).jobs
  },

  async listXeroSyncJobRuns(orgId: string, connectionId: string, jobId: string): Promise<XeroSyncJobRunPayload[]> {
    const url = `/api/lighthouse/organizations/${orgId}/integrations/xero/connections/${connectionId}/jobs/${jobId}/runs`
    const response = await http.get<{ runs: XeroSyncJobRunPayload[] }>(url)
    return unwrap(response).runs
  },

  async getXeroSyncJobRun(
    orgId: string,
    connectionId: string,
    jobId: string,
    runId: string,
  ): Promise<XeroSyncJobRunDetailPayload> {
    const url = `/api/lighthouse/organizations/${orgId}/integrations/xero/connections/${connectionId}/jobs/${jobId}/runs/${runId}`
    const response = await http.get<XeroSyncJobRunDetailPayload>(url)
    return unwrap(response)
  },

  async listXeroSyncJobRunSteps(
    orgId: string,
    connectionId: string,
    jobId: string,
    runId: string,
    type: string,
  ): Promise<XeroSyncJobRunStepPayload[]> {
    const url = `/api/lighthouse/organizations/${orgId}/integrations/xero/connections/${connectionId}/jobs/${jobId}/runs/${runId}/steps?type=${encodeURIComponent(type)}`
    const response = await http.get<{ steps: XeroSyncJobRunStepPayload[] }>(url)
    return unwrap(response).steps
  },

  async getXeroPipeline(orgId: string, connectionId: string, jobId: string): Promise<XeroIntegrationPipelinePayload> {
    const url = `/api/lighthouse/organizations/${orgId}/integrations/xero/connections/${connectionId}/jobs/${jobId}/pipeline`
    const response = await http.get<XeroIntegrationPipelinePayload>(url)
    return unwrap(response)
  },

  /**
   * List pipeline transactions for a job.
   */
  async listXeroPipelineTransactions(
    orgId: string,
    connectionId: string,
    jobId: string,
  ): Promise<XeroIntegrationPipelineTransactionPayload[]> {
    const url = `/api/lighthouse/organizations/${orgId}/integrations/xero/connections/${connectionId}/jobs/${jobId}/pipeline/transactions`
    const response = await http.get<{ transactions: XeroIntegrationPipelineTransactionPayload[] }>(url)
    return unwrap(response).transactions
  },

  /**
   * Get sync logs for a specific transaction.
   */
  async getXeroPipelineTransactionLogs(
    orgId: string,
    connectionId: string,
    jobId: string,
    transactionId: string,
    stage: string,
  ): Promise<XeroIntegrationPipelineStageLogPayload[]> {
    const url = `/api/lighthouse/organizations/${orgId}/integrations/xero/connections/${connectionId}/jobs/${jobId}/pipeline/transactions/${transactionId}/logs?stage=${stage}`
    const response = await http.get<XeroIntegrationPipelineStageLogPayload[]>(url)
    return unwrap(response)
  },

  createVessel(orgId: string, body: CreateVesselBody, config?: HttpConfig): Promise<Vessel> {
    return http.post<Vessel>(`/api/pay/org/${orgId}/vessel`, body, config).then(unwrap)
  },

  /**
   * List vessels the user has access to within an organization.
   */
  async listOrganizationVessels(
    orgId: string,
    params?: ListVesselsParams,
    config?: HttpConfig,
  ): Promise<{ vessels: VesselConnectedAccountAccess[] }> {
    const url = `/api/lighthouse/organizations/${orgId}/vessels`
    const searchParams = new URLSearchParams()
    if (params?.take !== undefined) {
      searchParams.set('take', String(params.take))
    }
    if (params?.skip !== undefined) {
      searchParams.set('skip', String(params.skip))
    }
    const query = searchParams.toString()
    return http.get<{ vessels: VesselConnectedAccountAccess[] }>(query ? `${url}?${query}` : url, config).then(unwrap)
  },

  /**
   * Get a vessel and its connected accounts.
   */
  async getVesselAndConnectedAccounts(
    orgId: string,
    vesselId: string,
    config?: HttpConfig,
  ): Promise<{ vesselAndConnectedAccounts: VesselAndConnectedAccounts }> {
    const url = `/api/pay/org/${orgId}/vessel/${vesselId}/connected-accounts`
    return http.get<{ vesselAndConnectedAccounts: VesselAndConnectedAccounts }>(url, config).then(unwrap)
  },

  async getConnectedAccountCards(orgId: string, vesselId: string, connectedAccountId: string) {
    const url = `/api/lighthouse/organizations/${orgId}/vessel/${vesselId}/connected-account/${connectedAccountId}/cards`
    return http.get<CardPayload[]>(url).then(unwrap)
  },

  async getConnectedAccountCardCheck(orgId: string, vesselId: string, connectedAccountId: string, cardId: string) {
    const url = `/api/lighthouse/organizations/${orgId}/vessels/${vesselId}/connected-account/${connectedAccountId}/card/${cardId}/check`
    return http.get<[CardPayload | null, AWXCardResponse]>(url).then(unwrap)
  },

  async backfillConnectedAccountCard(orgId: string, vesselId: string, connectedAccountId: string, cardId: string) {
    const url = `/api/lighthouse/organizations/${orgId}/vessels/${vesselId}/connected-account/${connectedAccountId}/card/${cardId}/backfill`
    return http.post<CardPayload>(url).then(unwrap)
  },

  async reconcileConnectedAccountCards(orgId: string, vesselId: string, connectedAccountId: string) {
    const url = `/api/lighthouse/organizations/${orgId}/vessels/${vesselId}/connected-account/${connectedAccountId}/cards/reconcile`
    return http.get<ReconciledCardPayload[]>(url).then(unwrap)
  },

  async reconcileConnectedAccountCardStatus(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    cardId: string,
  ) {
    const url = `/api/lighthouse/organizations/${orgId}/vessels/${vesselId}/connected-account/${connectedAccountId}/card/${cardId}/reconcile-status`
    return http.post<CardPayload>(url).then(unwrap)
  },

  async listConnectedAccountGlobalAccounts(orgId: string, vesselId: string, connectedAccountId: string) {
    const url = `/api/lighthouse/organizations/${orgId}/vessels/${vesselId}/connected-account/${connectedAccountId}/global-accounts`
    return http.get<LighthouseGlobalAccountPayload[]>(url).then(unwrap)
  },

  /**
   * Generate the official Airwallex statement letter for a global account.
   * @returns the statement PDF and its file name
   */
  async generateConnectedAccountStatement(
    orgId: string,
    vesselId: string,
    connectedAccountId: string,
    globalAccountId: string,
  ): Promise<{ blob: Blob; fileName: string }> {
    const url = `/api/lighthouse/organizations/${orgId}/vessels/${vesselId}/connected-account/${connectedAccountId}/global-accounts/${globalAccountId}/statement`
    // Airwallex generates the PDF on request, allow longer than the default timeout
    const response = await http.post<Blob>(url, undefined, { responseType: 'blob', timeout: 60000 })
    const fileName =
      parseFileName(response.headers['content-disposition']) ?? `account-statement-${globalAccountId}.pdf`
    return { blob: response.data, fileName }
  },

  /**
   * Activate a module for a vessel.
   * Backend automatically determines which module to activate based on what's already active.
   * Backend gets accountId from authenticated user context.
   * @returns connectedAccountId string
   */
  async activateModule(orgId: string, vesselId: string, config?: HttpConfig): Promise<string> {
    const url = `/api/pay/org/${orgId}/vessel/${vesselId}/activate`
    return http.post<string>(url, {}, config).then(unwrap)
  },

  async retryXeroConnectionSync(
    orgId: string,
    connectionId: string,
    jobId: string,
  ): Promise<XeroConnectionSyncRequest> {
    const url = `/api/lighthouse/organizations/${orgId}/integrations/xero/connections/${connectionId}/jobs/${jobId}/retry`
    const response = await http.post<XeroConnectionSyncRequest>(url)
    return unwrap(response)
  },

  // ─── Report generation pipelines ───────────────────────────────────────────

  async listReportGenerationJobs(
    orgId: string,
    params?: { vesselId?: string; connectedAccountId?: string; statuses?: GeneratedReportStatus[] },
  ): Promise<ReportGenerationJobPayload[]> {
    const searchParams = new URLSearchParams()
    if (params?.vesselId) {
      searchParams.set('vesselId', params.vesselId)
    }
    if (params?.connectedAccountId) {
      searchParams.set('connectedAccountId', params.connectedAccountId)
    }
    if (params?.statuses?.length) {
      for (const s of params.statuses) {
        searchParams.append('statuses', s)
      }
    }
    const query = searchParams.toString()
    const base = `/api/lighthouse/organizations/${orgId}/generated-reports/jobs`
    const response = await http.get<{ jobs: ReportGenerationJobPayload[] }>(query ? `${base}?${query}` : base)
    return unwrap(response).jobs
  },

  async listReportGenerationJobRuns(
    orgId: string,
    jobId: string,
    params?: { take?: number; skip?: number },
  ): Promise<Run<ReportGenerationPipelineData>[]> {
    const searchParams = new URLSearchParams()
    if (params?.take !== undefined) {
      searchParams.set('take', String(params.take))
    }
    if (params?.skip !== undefined) {
      searchParams.set('skip', String(params.skip))
    }
    const query = searchParams.toString()
    const base = `/api/lighthouse/organizations/${orgId}/generated-reports/jobs/${jobId}/runs`
    const response = await http.get<{ runs: Run<ReportGenerationPipelineData>[] }>(query ? `${base}?${query}` : base)
    return unwrap(response).runs
  },

  async getReportGenerationJobRun(
    orgId: string,
    jobId: string,
    runId: string,
  ): Promise<Run<ReportGenerationPipelineData>> {
    const url = `/api/lighthouse/organizations/${orgId}/generated-reports/jobs/${jobId}/runs/${runId}`
    const response = await http.get<Run<ReportGenerationPipelineData>>(url)
    return unwrap(response)
  },

  async listReportGenerationRunSteps(
    orgId: string,
    jobId: string,
    runId: string,
    type: string,
    params?: { take?: number; skip?: number },
  ): Promise<RunStep<string, Record<string, unknown>>[]> {
    const searchParams = new URLSearchParams()
    searchParams.set('type', type)
    if (params?.take !== undefined) {
      searchParams.set('take', String(params.take))
    }
    if (params?.skip !== undefined) {
      searchParams.set('skip', String(params.skip))
    }
    const url = `/api/lighthouse/organizations/${orgId}/generated-reports/jobs/${jobId}/runs/${runId}/steps?${searchParams.toString()}`
    const response = await http.get<{ steps: RunStep<string, Record<string, unknown>>[] }>(url)
    return unwrap(response).steps
  },

  /**
   * Fetches the vessel metrics for the admin dashboard.
   */
  async getVesselMetrics(): Promise<VesselMetricsPayload> {
    const response = await http.get<VesselMetricsPayload>('/api/lighthouse/dashboard/vessel-metrics')
    return unwrap(response)
  },

  /**
   * Fetches onboarding health KPIs (current totals) for the admin dashboard.
   */
  async getOnboardingHealth(): Promise<OnboardingHealthPayload> {
    const response = await http.get<OnboardingHealthPayload>('/api/lighthouse/dashboard/onboarding-health')
    return unwrap(response)
  },

  /**
   * Fetches transactions stats (totals, today, 7-day trend) for the admin dashboard.
   */
  async getTransactionsStats(): Promise<TransactionsStatsPayload> {
    const response = await http.get<TransactionsStatsPayload>('/api/lighthouse/dashboard/transactions-stats')
    return unwrap(response)
  },

  /**
   * Fetches transaction volume by currency for the admin dashboard.
   */
  async getTransactionVolume(): Promise<TransactionVolumePayload> {
    const response = await http.get<TransactionVolumePayload>('/api/lighthouse/dashboard/transaction-volume')
    return unwrap(response)
  },
}
